import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { evidenceAPI, projectsAPI } from '../services/api';
import { 
  Image, 
  FileText, 
  FileSpreadsheet,
  Upload, 
  Trash2,
  Download,
  Eye,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const Evidences = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [evidences, setEvidences] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '');
  const [activeTab, setActiveTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    file: null,
    evidence_type: 'photo',
    description: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchEvidences();
    }
  }, [selectedProject, activeTab]);

  const fetchProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      const projects = response?.data?.data || response?.data || [];
      setProjects(projects);
      if (projects.length > 0 && !selectedProject) {
        setSelectedProject(projects[0].id);
      }
    } catch (error) {
      console.error('Error al cargar proyectos:', error);
      toast.error('Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvidences = async () => {
    try {
      const type = activeTab !== 'all' ? activeTab : null;
      const response = await evidenceAPI.getByProject(selectedProject, type);
      const evidencesData = response?.data?.data || response?.data;
      const evidences = Array.isArray(evidencesData) ? evidencesData : [];
      setEvidences(evidences);
    } catch (error) {
      console.error('Error al cargar evidencias:', error);
      toast.error('Error al cargar evidencias');
      setEvidences([]);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, file });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file) {
      toast.error('Selecciona un archivo');
      return;
    }

    setUploading(true);
    
    try {
      const data = new FormData();
      data.append('file', formData.file);
      data.append('evidence_type', formData.evidence_type);
      data.append('description', formData.description);

      await evidenceAPI.upload(selectedProject, data);
      toast.success('Evidencia subida');
      setDialogOpen(false);
      setFormData({ file: null, evidence_type: 'photo', description: '' });
      fetchEvidences();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir evidencia');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await evidenceAPI.delete(deleteId);
      toast.success('Evidencia eliminada');
      fetchEvidences();
    } catch (error) {
      toast.error('Error al eliminar evidencia');
    } finally {
      setDeleteId(null);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'photo': return <Image className="w-5 h-5" />;
      case 'document': return <FileSpreadsheet className="w-5 h-5" />;
      case 'plan': return <FileText className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeBadge = (type) => {
    const styles = {
      photo: 'bg-blue-100 text-blue-800',
      document: 'bg-green-100 text-green-800',
      plan: 'bg-purple-100 text-purple-800'
    };
    const labels = {
      photo: 'Foto',
      document: 'Documento',
      plan: 'Plano'
    };
    return (
      <Badge className={styles[type] || styles.document}>
        {labels[type] || type}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-12 w-full max-w-md" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Skeleton key={i} className="aspect-square rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="evidences-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">Evidencias</h1>
            <p className="text-[#5C554B]">Fotos, documentos y planos de obra</p>
          </div>
          {selectedProject && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="upload-btn">
                  <Upload className="w-5 h-5 mr-2" />
                  Subir Evidencia
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Subir Evidencia</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Tipo de evidencia *</Label>
                    <Select 
                      value={formData.evidence_type} 
                      onValueChange={(value) => setFormData({ ...formData, evidence_type: value })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="photo">Foto (.jpg, .jpeg, .png)</SelectItem>
                        <SelectItem value="document">Documento (.xls, .xlsx, .pdf)</SelectItem>
                        <SelectItem value="plan">Plano (.dwg, .pdf, .dxf)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="file">Archivo *</Label>
                    <Input
                      id="file"
                      type="file"
                      onChange={handleFileChange}
                      accept={
                        formData.evidence_type === 'photo' 
                          ? '.jpg,.jpeg,.png,.webp'
                          : formData.evidence_type === 'document'
                            ? '.xls,.xlsx,.pdf,.doc,.docx'
                            : '.dwg,.pdf,.dxf'
                      }
                      className="h-12 pt-3"
                    />
                    {formData.file && (
                      <p className="text-sm text-[#81A183]">{formData.file.name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Descripción</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descripción de la evidencia..."
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="btn-primary" disabled={uploading}>
                      {uploading ? 'Subiendo...' : 'Subir'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Project Selector */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <Label className="mb-2 block">Seleccionar Obra</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="h-12 bg-white max-w-md">
                <SelectValue placeholder="Selecciona una obra" />
              </SelectTrigger>
              <SelectContent>
                {Array.isArray(projects) && projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#EBE5DA]">
            <TabsTrigger value="all" className="data-[state=active]:bg-white">
              Todas
            </TabsTrigger>
            <TabsTrigger value="photo" className="data-[state=active]:bg-white">
              <Image className="w-4 h-4 mr-2" />
              Fotos
            </TabsTrigger>
            <TabsTrigger value="document" className="data-[state=active]:bg-white">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Documentos
            </TabsTrigger>
            <TabsTrigger value="plan" className="data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              Planos
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {!Array.isArray(evidences) || evidences.length === 0 ? (
              <Card className="card-dashboard">
                <CardContent className="py-12 text-center">
                  <Image className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-[#2D2A26] mb-2">No hay evidencias</h3>
                  <p className="text-[#5C554B]">Sube la primera evidencia para esta obra</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {Array.isArray(evidences) && evidences.map((evidence) => (
                  <Card key={evidence.id} className="card-dashboard group overflow-hidden">
                    <CardContent className="p-0">
                      <div className="relative aspect-square">
                        {evidence.evidence_type === 'photo' ? (
                          <img
                            src={evidenceAPI.getFileUrl(evidence.id)}
                            alt={evidence.description || 'Evidencia'}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#FDFBF7]">
                            {getTypeIcon(evidence.evidence_type)}
                            <span className="text-xs text-[#5C554B] text-center px-2 mt-2 line-clamp-2">
                              {evidence.file_name}
                            </span>
                          </div>
                        )}
                        
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          {evidence.evidence_type === 'photo' ? (
                            <Button
                              size="icon"
                              variant="secondary"
                              onClick={() => setPreviewUrl(evidenceAPI.getFileUrl(evidence.id))}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          ) : null}
                          <a 
                            href={evidenceAPI.getFileUrl(evidence.id)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <Button size="icon" variant="secondary">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button
                            size="icon"
                            variant="secondary"
                            onClick={() => setDeleteId(evidence.id)}
                            className="hover:bg-red-100"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>

                        {/* Badge */}
                        <div className="absolute top-2 left-2">
                          {getTypeBadge(evidence.evidence_type)}
                        </div>
                      </div>
                      
                      <div className="p-3">
                        <p className="text-sm text-[#2D2A26] line-clamp-1">
                          {evidence.description || evidence.file_name}
                        </p>
                        <p className="text-xs text-[#9CA3AF] mt-1">
                          {new Date(evidence.created_at).toLocaleDateString('es-MX')}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Image Preview Modal */}
      {previewUrl && (
        <Dialog open={!!previewUrl} onOpenChange={() => setPreviewUrl(null)}>
          <DialogContent className="max-w-4xl p-0">
            <button
              onClick={() => setPreviewUrl(null)}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white hover:bg-black/70"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={previewUrl}
              alt="Preview"
              className="w-full h-auto max-h-[80vh] object-contain"
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar esta evidencia?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
};

export default Evidences;
